import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BarChart3, Building2, ThumbsUp, ThumbsDown, ChevronDown, ChevronUp, Info, Repeat } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${Math.round(n || 0).toLocaleString('en-CA')}`;
const compact = (n) => (n >= 1e6 ? `$${(n / 1e6).toFixed(2)}M` : n >= 1e3 ? `$${Math.round(n / 1e3)}K` : money(n));
const pct = (n) => `${Math.round(n)}%`;
const RANK = ['bg-amber-400 text-amber-950', 'bg-slate-300 text-slate-900', 'bg-orange-400 text-white'];

const tone = (m, s) => (m >= (s?.margin_target_pct ?? 35) ? 'text-emerald-700' : m >= (s?.margin_warn_pct ?? 20) ? 'text-amber-700' : 'text-red-700');

const Panel = ({ icon: Icon, title, sub, children, testid }) => (
  <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-4 min-w-0" data-testid={testid}>
    <div className="flex items-center gap-2">
      <Icon className="w-4 h-4 text-slate-500" />
      <h3 className="text-xs font-bold uppercase tracking-[0.14em] text-slate-600">{title}</h3>
      {sub && <span className="ml-auto text-[10px] text-slate-400">{sub}</span>}
    </div>
    <div className="mt-3 space-y-1.5">{children}</div>
  </div>
);

const JobRow = ({ j, settings, onOpen }) => (
  <button type="button" onClick={() => onOpen(j.wo)} className="w-full text-left flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-slate-50" data-testid={`analytics-job-${j.wo}`}>
    <span className="font-mono text-xs font-bold text-blue-700 shrink-0">{j.wo}</span>
    <span className="flex-1 min-w-0">
      <span className="block text-sm font-semibold text-slate-900 truncate">{j.customer || '—'}</span>
      <span className="block text-[11px] text-slate-500 truncate">{j.description || ''}{j.pm ? ` · ${j.pm}` : ''}</span>
    </span>
    <span className="text-right shrink-0">
      <span className={`block font-mono text-sm font-black ${tone(j.margin_pct, settings)}`}>{pct(j.margin_pct)}</span>
      <span className="block text-[10px] text-slate-500">{compact(j.revenue)} billed</span>
    </span>
  </button>
);

// Top of the Projects page: top customers by invoiced sales, best / worst jobs by estimated margin for the chosen fiscal year.
export const ProjectsAnalytics = ({ year }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(() => localStorage.getItem('projects_analytics_open') !== '0');
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/projects-analytics`, { params: year ? { year } : {} }).then(r => { if (alive) setData(r.data); }).catch(() => { if (alive) setData(null); });
    return () => { alive = false; };
  }, [year]);
  useEffect(() => { localStorage.setItem('projects_analytics_open', open ? '1' : '0'); }, [open]);
  if (!data) return null;
  const t = data.totals;
  const openJob = (wo) => navigate(`/projects/${wo}`);
  return (
    <section className="mx-6 mb-6 rounded-3xl border border-slate-200/90 bg-[#F8FAFD]/95 backdrop-blur-md shadow-[0_2px_12px_-2px_rgba(15,23,42,0.08)] p-4 sm:p-5" data-testid="projects-analytics">
      <div className="flex flex-wrap items-center gap-3">
        <span className="h-9 w-9 rounded-xl bg-slate-900 text-sky-300 flex items-center justify-center shrink-0"><BarChart3 className="w-4 h-4" /></span>
        <div className="min-w-0">
          <h2 className="text-lg md:text-xl font-extrabold text-slate-900 tracking-tight leading-tight">FY{data.long} at a glance</h2>
          <p className="text-[11px] text-slate-500 font-medium" data-testid="analytics-totals">
            {compact(t.invoiced)} invoiced · {t.jobs} job{t.jobs === 1 ? '' : 's'} · {t.customers} customer{t.customers === 1 ? '' : 's'}
            {t.avg_margin_pct != null && <> · avg margin <b className={tone(t.avg_margin_pct, data.settings)}>{pct(t.avg_margin_pct)}</b> on {t.jobs_costed} costed job{t.jobs_costed === 1 ? '' : 's'}{data.scope === 'own' ? ' (yours)' : ''}</>}
          </p>
        </div>
        <button type="button" onClick={() => setOpen(o => !o)} className="ml-auto h-8 w-8 rounded-full bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 flex items-center justify-center" data-testid="analytics-toggle" title={open ? 'Collapse' : 'Expand'}>
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>
      {data.partial_note && (
        <p className="mt-3 inline-flex items-start gap-1.5 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 px-3 py-1.5 text-xs" data-testid="analytics-partial-note">
          <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {data.partial_note}
        </p>
      )}
      {open && (
        <div className="mt-4 grid gap-3 lg:grid-cols-2 2xl:grid-cols-4">
          <Panel icon={Building2} title="Top customers" sub="by invoiced sales · spellings merged" testid="analytics-top-customers">
            {data.top_customers.length === 0 && <p className="text-sm text-slate-500">Nothing invoiced in FY{data.long} yet.</p>}
            {data.top_customers.map((c, i) => (
              <div key={c.customer} className="flex items-center gap-2 px-2 py-1.5" data-testid={`analytics-customer-${i + 1}`}>
                <span className={`h-6 w-6 rounded-full text-[11px] font-black flex items-center justify-center shrink-0 ${RANK[i] || 'bg-slate-200 text-slate-700'}`}>{i + 1}</span>
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-semibold text-slate-900 truncate">{c.customer}</span>
                  {c.also_as?.length > 0 && <span className="block text-[10px] text-slate-400 truncate" data-testid={`analytics-customer-${i + 1}-aliases`}>also billed as {c.also_as.join(', ')}</span>}
                  <span className="block h-1 mt-1 rounded-full bg-slate-100 overflow-hidden"><span className="block h-full bg-sky-500 rounded-full" style={{ width: `${Math.max(4, c.share * 100)}%` }} /></span>
                </span>
                <span className="text-right shrink-0">
                  <span className="block font-mono text-sm font-black text-slate-900">{compact(c.revenue)}</span>
                  <span className="block text-[10px] text-slate-500">{c.jobs} job{c.jobs === 1 ? '' : 's'} · {pct(c.share * 100)}</span>
                </span>
              </div>
            ))}
          </Panel>
          <Panel icon={Repeat} title="Coming back" sub={`${data.returning_count} of ${data.last_year_customers} FY${data.last_long} customers`} testid="analytics-returning">
            {data.returning.length === 0 && <p className="text-sm text-slate-500">No FY{data.last_long} customer has been invoiced in FY{data.long} yet.</p>}
            {data.returning.map((r, i) => {
              const up = r.change_pct != null && r.change_pct >= 0;
              const chg = r.change_pct == null ? 'new' : `${up ? '▲' : '▼'} ${Math.abs(r.change_pct) > 999 ? '>999' : Math.abs(r.change_pct)}%`;
              return (
                <div key={r.customer} className="flex items-center gap-2 px-2 py-1.5" data-testid={`analytics-returning-${i + 1}`}>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-semibold text-slate-900 truncate">{r.customer}</span>
                    <span className="block text-[10px] text-slate-500">{r.this_jobs} job{r.this_jobs === 1 ? '' : 's'} now · {r.last_jobs} last year</span>
                  </span>
                  <span className="text-right shrink-0">
                    <span className="block font-mono text-sm font-black text-slate-900">{compact(r.this_revenue)}</span>
                    <span className={`block text-[10px] font-bold ${up ? 'text-emerald-700' : 'text-red-700'}`}>{chg} <span className="font-normal text-slate-400">vs {compact(r.last_revenue)}</span></span>
                  </span>
                </div>
              );
            })}
          </Panel>
          <Panel icon={ThumbsUp} title="Best jobs" sub={`by margin · over ${compact(data.min_job_revenue)}`} testid="analytics-best-jobs">
            {data.best_jobs.length === 0 && <p className="text-sm text-slate-500">No costed jobs yet — margins appear once hours are logged to invoiced WOs.</p>}
            {data.best_jobs.map(j => <JobRow key={j.wo} j={j} settings={data.settings} onOpen={openJob} />)}
          </Panel>
          <Panel icon={ThumbsDown} title="Worst jobs" sub={`by margin · over ${compact(data.min_job_revenue)}`} testid="analytics-worst-jobs">
            {data.worst_jobs.length === 0 && <p className="text-sm text-slate-500">No costed jobs yet.</p>}
            {data.worst_jobs.map(j => <JobRow key={j.wo} j={j} settings={data.settings} onOpen={openJob} />)}
          </Panel>
        </div>
      )}
    </section>
  );
};
